import { FC, useState, useEffect, useMemo } from "react";

import {
  Row,
  Column,
  IconButton,
  SearchInput,
  Button,
  Spinner,
  RefreshIcon,
  Dialog,
  EmptyState,
  Tooltip,
} from "@hightouch/htui";

import { Link } from "src/router";

import { useDbtSyncModelsQuery } from "src/graphql";
import * as analytics from "src/lib/analytics";
import { DBTIcon } from "src/ui/icons";

import { Editor } from "src/components/editor";
import { DbtQuery } from "./preview/dbt-query";
import { SelectorRow } from "./selector-row";
import { QueryContainer, QueryHeader } from "src/components/query";

type Source = {
  id: string;
  name: string;
  type: string;
  definition: {
    name: string;
    icon: string;
    isSampleDataSource: boolean;
  };
};

type Props = {
  dbtModelId: number | null | undefined;
  onChange: (dbtModel: number) => void;
  source: Source;
  filter: "core" | "cloud";
};

const getDisplayName = (model: {
  schema: string;
  name: string;
  data?: { alias: string };
}): string => `${model.schema}.${model.data?.alias || model.name}`;

export const DBTModelSelector: FC<Readonly<Props>> = ({
  dbtModelId,
  onChange,
  source,
  filter = "core",
}) => {
  const [search, setSearch] = useState<string>("");
  const [showSQL, setShowSQL] = useState<boolean>(false);

  const {
    data,
    isFetching: loading,
    refetch,
  } = useDbtSyncModelsQuery(
    {
      sourceId: source?.id,
    },
    {
      enabled: Boolean(source?.id),
    },
  );

  const filteredModels = useMemo(() => {
    const includeCloud = filter === "cloud";
    const includeCore = filter === "core";

    return (
      (!loading &&
        data?.dbt_sync_models?.filter(
          (model) =>
            (includeCloud && model.dbt_cloud_sync_config?.id) ||
            (includeCore && model.dbt_sync_config?.id),
        )) ||
      []
    );
  }, [data?.dbt_sync_models, filter, loading]);
  const noDBTModels = !loading && filteredModels.length == 0;

  const dbtModel = filteredModels.find(({ id }) => id === dbtModelId);
  const available = filteredModels.filter((model) => model?.removed !== true);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (noDBTModels && !loading) {
      analytics.track("Add Model Query Mode Error", {
        source_type: source?.type,
        source_name: source?.name,
        query_mode: "dbt_model",
        error: "No dbt models were synced",
      });
    }
  }, [loading, noDBTModels]);

  if (noDBTModels) {
    const cloudError = {
      title:
        "No dbt models were synced from the dbt Cloud job associated with this source.",
      message: (
        <>
          Please configure a dbt Cloud account on the{" "}
          <Link href="/extensions/dbt-cloud">settings page</Link> and select a
          job for your <Link href={`/sources/${source?.id}`}>source</Link>.
        </>
      ),
    };

    const gitError = {
      title:
        "No dbt models were synced from the Git repository associated with this source.",
      message: (
        <>
          Please add your Git credentials in the{" "}
          <Link href="/extensions/dbt-models">settings page</Link> and select a
          repository in your <Link href={`/sources/${source?.id}`}>source</Link>
          .
        </>
      ),
    };

    const error = filter == "core" ? gitError : cloudError;

    return <EmptyState messageMaxW="100%" {...error} />;
  }

  const models = search
    ? available?.filter((model) => {
        const lowercaseSearch = search.toLowerCase();
        const displayName = getDisplayName(model);
        return displayName.toLowerCase()?.includes(lowercaseSearch);
      })
    : available;

  const isPlaceholder = loading || !models?.length;

  return (
    <>
      <Column overflow="hidden" flex={1} minWidth={0}>
        <Row overflow="hidden" gap={8}>
          <QueryContainer>
            <QueryHeader>
              <SearchInput
                placeholder="Search dbt models…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Tooltip message="Refresh to populate new dbt models">
                <IconButton
                  aria-label="Refresh dbt models"
                  variant="secondary"
                  icon={RefreshIcon}
                  isLoading={loading}
                  onClick={() => {
                    refetch();
                  }}
                />
              </Tooltip>
            </QueryHeader>
            <Column
              overflow="auto"
              flex={1}
              minWidth={0}
              p={isPlaceholder ? 10 : undefined}
              align={isPlaceholder ? "center" : undefined}
              justify={isPlaceholder ? "center" : undefined}
            >
              {loading ? (
                <Spinner size="lg" />
              ) : !models?.length ? (
                <>
                  <EmptyState
                    variant="minimal"
                    title={
                      search
                        ? "No dbt models match your search"
                        : "No dbt models found"
                    }
                    message="Refresh to populate new dbt models"
                    actions={
                      <Button
                        onClick={() => {
                          refetch();
                        }}
                      >
                        Refresh
                      </Button>
                    }
                  />
                </>
              ) : (
                <>
                  {models?.map((model) => {
                    const active = model?.id === dbtModelId;
                    return (
                      <SelectorRow
                        key={model?.id}
                        icon={
                          <DBTIcon
                            size={16}
                            color="var(--chakra-colors-text-secondary)"
                          />
                        }
                        selected={active}
                        onClick={() => onChange(model?.id)}
                      >
                        {getDisplayName(model)}
                      </SelectorRow>
                    );
                  })}
                </>
              )}
            </Column>
          </QueryContainer>
          {dbtModel && (
            <DbtQuery
              model={dbtModel}
              actions={
                <Button
                  onClick={() => {
                    setShowSQL(true);
                  }}
                >
                  View SQL
                </Button>
              }
            />
          )}
        </Row>
        <Dialog
          isOpen={showSQL}
          variant="info"
          width="xl"
          title="SQL"
          actions={<Button onClick={() => setShowSQL(false)}>Close</Button>}
          onClose={() => setShowSQL(false)}
        >
          <Editor
            readOnly
            language="sql"
            value={dbtModel?.data?.compiled_sql}
          />
        </Dialog>
      </Column>
    </>
  );
};
